import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { ANNOUNCEMENT_COMPONENTS, firstRenderable } from '@/lib/announcements';

/**
 * THE QUEUE. Mounted once on the dashboard; shows at most one announcement.
 *
 * ORDER — written down here rather than emerging from whoever polls fastest:
 *
 *     1. Time survey         PROMPT        (EditorTimeSurvey.js, z-210, editors)
 *     2. Song Approval       ANNOUNCEMENT  (SongApprovalIntro.js, z-60, editors)
 *     2. Must-Haves intro    ANNOUNCEMENT  (MustHavesIntro.js, z-200, clients)
 *     3. This queue          ANNOUNCEMENT  (z-60, every role)
 *
 *   The time survey (2026-09-25) is the one live PROMPT. The footage-intake question was
 *   the one before it, retired on 2026-09-16 (KNOWN-ISSUES 0au).
 *
 * PROMPTS AND ANNOUNCEMENTS ARE TREATED DIFFERENTLY, AND MUST BE.
 *
 *   A PROMPT asks for something the product needs from this person now. It
 *   recurs until answered, because the answer is something the product cannot
 *   work without. We WAIT for it and then show — it is not
 *   subject to the one-per-login rule and must never be suppressed by it.
 *   Deferring a prompt behind an announcement would trade a needed answer for a
 *   nice-to-know.
 *
 *   An ANNOUNCEMENT says the product changed. Nobody is blocked by not having
 *   read one. Two in a single login reads as neglect, so if another announcement
 *   is already on screen we STAND DOWN for this login entirely rather than
 *   queueing behind it. Standing down is not the same as waiting: we write no
 *   session flag, so the announcement is still owed and arrives next login.
 *
 * ACCEPTED PROPERTY, NOT A BUG: announcements return until acknowledged, and only
 * one shows per login. So a user who never acknowledges the announcement at the
 * front of their queue blocks everything behind it, indefinitely. That is the
 * deliberate trade for "an announcement about how your job changed should not be
 * dismissable by ignoring it". If a queue of three ever builds up behind one
 * stubborn reader, that is the moment to revisit it — not before.
 */

const SESSION_KEY = 'ug_announcement_seen_this_session';

/** Everything that must be clear before an announcement may open, in order.
 *  `kind` decides the behaviour, and the two are genuinely different verbs. */
const BLOCKERS = [
  { testid: 'time-survey-prompt',      kind: 'prompt',       label: 'Time survey' },
  { testid: 'song-intro-modal',        kind: 'announcement', label: 'Song Approval' },
  { testid: 'mh-intro-modal',          kind: 'announcement', label: 'Must-Haves intro' },
  // The two other things a client can be looking at on arrival (Layout.js, ReviewAskModal).
  // Neither needs an answer, so we stand down rather than wait, and are owed next login.
  { testid: 'tier-celebration-overlay', kind: 'announcement', label: 'Tier celebration' },
  { testid: 'review-ask-modal',        kind: 'announcement', label: 'Review ask' },
];

const onScreen = (testid) => !!document.querySelector(`[data-testid="${testid}"]`);

export default function AnnouncementQueue() {
  const { user } = useAuth();
  const [key, setKey] = useState(null);

  useEffect(() => {
    // Every role asks, clients included since song_once_client (2026-09-15). The server
    // decides per role and, for that entry, per client: most clients are owed nothing.
    if (!user?.role) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;

    let timer;
    let cancelled = false;

    api.get('/announcements/pending')
      .then(r => {
        if (cancelled) return;
        const next = firstRenderable(r.data?.pending);
        if (!next) return;

        const settle = () => {
          if (cancelled) return;

          // A PROMPT on screen: wait for it. It is not ours to pre-empt, and it
          // will go away once answered.
          const prompt = BLOCKERS.find(b => b.kind === 'prompt' && onScreen(b.testid));
          if (prompt) { timer = setTimeout(settle, 800); return; }

          // ANOTHER ANNOUNCEMENT on screen: stand down for this login. No session
          // flag is written, so this one is still owed next time.
          if (BLOCKERS.some(b => b.kind === 'announcement' && onScreen(b.testid))) return;

          // Nothing above us. Claim the slot for this login and open.
          sessionStorage.setItem(SESSION_KEY, '1');
          setKey(next);
        };
        // One beat before the first check: the blockers above mount from their own
        // fetches, so an immediate look would see an empty screen and win a race it
        // should have lost.
        timer = setTimeout(settle, 700);
      })
      .catch(() => {});

    return () => { cancelled = true; clearTimeout(timer); };
  }, [user?.role]);

  // Body scroll lock — the announcement is blocking, so the page behind it
  // should not move.
  useEffect(() => {
    if (!key) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [key]);

  if (!key) return null;
  const Component = ANNOUNCEMENT_COMPONENTS[key];
  if (!Component) return null;

  /**
   * ACKNOWLEDGE FIRES ON THE BUTTON, AND ONLY ON THE BUTTON. This is a decision,
   * not an oversight — do not "fix" it by writing on open or on unmount.
   *
   * An announcement about a change to how editors work should return until it is
   * actually read. Closing the tab is therefore not an acknowledgement: the person
   * has not read it, so next login they are owed it again. The same reasoning is
   * why there is no close button, no Escape handler and no backdrop dismiss —
   * matching SongApprovalIntro, which is explicit that it "keeps returning until
   * the box is ticked".
   *
   * A FAILED WRITE STILL CLOSES THE MODAL. The cost of a failed POST is one repeat
   * showing; the cost of holding the modal open on a network error is trapping
   * someone behind something they have already read and cannot dismiss. The
   * cheaper failure wins.
   *
   * THE ONE EXCEPTION IS THE COMPONENT'S TO DECLARE. A notice that blocks nobody's work (the
   * referral program, for clients) may say `dismissOnBackdrop`, and then a click on the
   * backdrop acknowledges it too (operator, 2026-09-23: every way out marks it seen). The
   * work-changing notices above say nothing and keep returning until their button is pressed.
   */
  const acknowledge = async () => {
    try { await api.post(`/announcements/${key}/acknowledge`); } catch { /* close anyway */ }
    setKey(null);
  };
  const onBackdrop = (e) => {
    if (Component.dismissOnBackdrop && e.target === e.currentTarget) acknowledge();
  };

  return (
    <div className="ug-modal fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
         role="dialog" aria-modal="true" data-testid="announcement-modal" onClick={onBackdrop}>
      <Component role={user.role} embedded onAcknowledge={acknowledge} />
    </div>
  );
}
